<template>
  <q-page
    :data-test="CypressTarget('UserEditPage')"
    padding
    v-bind="__cordovaFixQPageBind"
  >
    <div class="row justify-start gutter-sm">
      <div class="col-sm-12 col-md-6">
        <q-card
          :data-test="CypressTarget('UserCard')"
          class="relative-position"
        >
          <q-card-title>{{ $t('label.userData') }}</q-card-title>
          <q-card-main>
            <component
              :is="identificationForm.user"
              ref="userForm"
              :value="getUserInformation"
              :can-edit-cpf="canEditCPF"
              :can-edit-name="canEditName"
              :can-edit="true"
              :edit-mode="true"
              :imperial-system="isImperialSystem"
              @input="userData.information = $event"
            />
          </q-card-main>
          <q-inner-loading :visible="loading">
            <q-spinner
              :color="SET_COLOR.primary"
              size="50px"
            />
          </q-inner-loading>
        </q-card>
        <div class="opt-in-specific-fields">
          <form-user-extra
            ref="extraForm"
            :value="getUserExtras"
            :can-edit="true"
            :edit-mode="true"
            :unchecked-fields="false"
            @input="userData.extra = $event"
          />
        </div>
      </div>
      <div class="col-sm-12 col-md-6">
        <q-card
          :data-test="CypressTarget('AddressCard')"
          class="relative-position"
        >
          <q-card-title>{{ $t('label.userAddress') }}</q-card-title>
          <q-card-main>
            <component
              :is="identificationForm.address"
              ref="addressForm"
              :value="getUserAddress"
              :can-edit="true"
              :edit-mode="true"
              @input="userData.address = $event"
            />
          </q-card-main>
          <q-inner-loading :visible="loading">
            <q-spinner
              :color="SET_COLOR.primary"
              size="50px"
            />
          </q-inner-loading>
        </q-card>
      </div>
      <div
        :class="{'justify-end': $q.platform.is.desktop, 'justify-center': $q.platform.is.mobile}"
        :data-test="CypressTarget('Buttons')"
        class="row items-end col-12"
      >
        <div class="col-xs-12 col-md-6 row gutter-sm">
          <div class="col-6">
            <q-btn
              v-go-back.single=" '/' "
              :data-test="CypressTarget('Cancel')"
              :label="$t('button.cancel')"
              color="negative"
              icon="cancel"
              class="full-width"
            />
          </div>
          <div class="col-6">
            <q-btn
              :data-test="CypressTarget('Ok')"
              :label="$t('button.update')"
              :loading="loading"
              color="positive"
              icon="edit"
              class="full-width"
              @click.native="updateUser"
            />
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { UNITEDSTATES } from 'constants/countries';
  import CordovaIosFixes from 'mixins/fixes/ios/cordova';
  import { userDataDefault } from 'store/modules/user/state';

  export default {
    name: 'SubscriptionUserEdit',
    components: {
      FormUserExtra: () => import('components/form/user/extra'),
    },
    mixins: [CordovaIosFixes],
    data() {
      return {
        form: {
          brazilian: {
            user: () => import('components/form/user/brazilian'),
            address: () => import('components/form/address/brazilian'),
          },
          foreigner: {
            user: () => import('components/form/user/foreigner'),
            address: () => import('components/form/address/foreigner'),
          },
        },
        userData: { ...userDataDefault },
        canEditCPF: false,
        canEditName: false,
      };
    },
    computed: {
      ...mapGetters('user', [
        'getUserAddress',
        'getUserInformation',
        'getUserExtras',
        'isBrazilian',
        'loading',
      ]),
      ...mapGetters('settings', [
        'getIntiModule',
      ]),
      isImperialSystem() {
        return this.userData.address.country === UNITEDSTATES;
      },
      userLanguage() {
        if (this.isBrazilian) {
          return 'pt-br';
        }
        return this.SET_LANGUAGE;
      },
      identificationForm() {
        if (this.userData.information.isForeigner === false || this.getIntiModule !== 'ticket') {
          return this.form.brazilian;
        }
        return this.form.foreigner;
      },
    },
    watch: {
      userData: {
        handler(data) {
          if (!this.loading) {
            this.changeUserState(data);
          }
        },
        deep: true,
        imediate: true,
      },
    },
    async created() {
      document.title = `${this.SET_PAGE_NAME} | ${this.$t('page.userEdit')}`;
      try {
        await this.getUserData();
        this.userData.address = this.getUserAddress;
        this.userData.information = this.getUserInformation;
        this.canEditCPF = this.userData.information.cpf === '';
        this.canEditName = this.userData.information.name === '';
      } catch (error) {
        this.toastError(error);
      }
    },
    mounted() {
      this.$tracker.pageView({
        page: this.$route.path,
        title: this.$t('page.userEdit'),
      });
    },
    methods: {
      ...mapActions('user', [
        'getUserData',
        'updateUserData',
        'changeUserState',
      ]),
      updateUser() {
        if (!this.$refs.userForm || !this.$refs.addressForm || !this.$refs.extraForm) return false;

        this.$refs.userForm.touchForm();
        this.$refs.addressForm.touchForm();

        if (this.$refs.userForm.validateForm()
          || this.$refs.addressForm.validateForm()
          || this.$refs.extraForm.validateForm()) {
          return false;
        }

        const user = this.userData;
        delete user.information.name;
        delete user.information.cpf;
        user.information.isEditingUser = true;

        this.updateUserData(user)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              icon: 'check_circle',
              message: this.$t('alert.userUpdated.success'),
              timeout: 3000,
            });
              this.$router.go(-1);
          })
          .catch((error) => this.toastError(error));
        return true;
      },
    },
  };
</script>
